import {
  getGetV8CubridSqlTextQueryKey,
  getV8CubridSqlText,
} from '@/api-client/cubrid/queries/cubrid-controller/cubrid-controller';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { DB_TYPE } from '@/common/define/common.define';
import { DATABASE_DETAIL_TYPE, DOCUMENT_TEXT_TYPE } from '@/database/adapter/openDetail.define';
import type { DetailParams, DocumentTextParams } from '../types';
import { formatName, mergeDetailInfo, parseSchema } from './utils';
import { getRequiredMap } from '@/common/utils/core/detailFieldMeta.utils';
import {
  brokerDetailSchema,
  documentTextSchema,
  type BrokerDetailAddition,
  type BrokerDetailSchema,
  type DocumentTextSchema,
} from './cubrid.schema';
import { formatTimeRange } from '@/database/utils/format.utils';

export const getCubridBrokerDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
}: DetailParams): { name: string; addition: BrokerDetailAddition } => {
  const { data } = parseSchema<BrokerDetailSchema>(
    brokerDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime }),
    DATABASE_DETAIL_TYPE.BROKER,
  );

  return {
    name: formatName(data.brokerId),
    addition: {
      instanceId: data.instanceId,
      dbType: DB_TYPE.CUBRID,
      fromTime: data.fromTime,
      toTime: data.toTime,
    },
  };
};

export const getCubridDocumentTextParams = ({
  detailInfo,
  instanceId,
  fromTime,
  toTime,
  period,
  useFieldValidation,
}: DocumentTextParams) => {
  const formattedTime = formatTimeRange({ fromTime, toTime });
  const timeInfo = period ? { period } : formattedTime;

  if (!useFieldValidation) {
    return {
      instanceId,
      sqlId: detailInfo.sqlId,
      ...timeInfo,
    };
  }

  const { data } = parseSchema<DocumentTextSchema>(
    documentTextSchema,
    mergeDetailInfo(detailInfo, instanceId),
    DOCUMENT_TEXT_TYPE,
  );

  return {
    instanceId: data.instanceId,
    sqlId: data.sqlId,
    ...timeInfo,
  };
};

export const fetchCubridDocumentText = async ({
  frameName,
  abortController,
  ...rest
}: DocumentTextParams) => {
  const params = getCubridDocumentTextParams({ ...rest, useFieldValidation: true });

  const {
    data: { data = [] },
  } = await getV8CubridSqlText(
    params,
    { params: { frameName: frameName || FRAME_NAMES.DOCUMENT_WINDOW.FULL_TEXT } },
    abortController?.signal,
  );
  return data[0]?.data?.[0]?.sqlText || null;
};

export const getCubridDocumentTextQueryKey = (params: DocumentTextParams | null) => {
  if (!params) {
    return getGetV8CubridSqlTextQueryKey();
  }

  const convertedParams = getCubridDocumentTextParams({ ...params, useFieldValidation: false });

  return getGetV8CubridSqlTextQueryKey(convertedParams);
};

export const cubridBrokerDetailRequiredMap = getRequiredMap(brokerDetailSchema);
export const cubridDocumentTextRequiredMap = getRequiredMap(documentTextSchema);
